import { action, makeObservable } from "mobx";
import { MessageType } from "src/components/notifications/Notifier";
import { RootStore } from "src/stores/RootStore";
import i18next from "i18next";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleId, getRoleKey, Role } from "src/network/User";
import { GetFileInfoById, GetFileInfoById_file, GetFileInfoByIdVariables } from "src/api/generated/GetFileInfoById";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GET_FILE_INFO_BY_ID } from "src/api/cred";
import Axios from "src/network/Axios";
import { AxiosResponse } from "axios";
import { IFile } from "src/types";
import { FILE_ENTITY, FILE_ORIGIN } from "src/enums";

interface FileUploadResult {
    FileId: string;
    UploadUrl: string;
    Extension: string;
}

export class FileStore {
    rootStore: RootStore;
    CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB pro Block (max 100 MB, max 50'000 Blöcke)

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            downloadFile: action,
            downloadFileAndTriggerDownload: action,
            getFileInfo: action,
            triggerDownload: action
        });
    }

    downloadFile = async (fileId: string): Promise<AxiosResponse<Blob> | undefined> => {
        let downloadedInvoiceFile = undefined;

        try {
            const role = this.rootStore.authStore.user?.role;

            const downloadInvoiceFileResponse = await Axios.getAuthInstance().get(
                `${NetworkConfig.downloadFileVersion2}/${fileId}`,
                {
                    urlWithoutParams: NetworkConfig.downloadFileVersion2,
                    responseType: "blob",
                    headers: {
                        "x-hasura-role": getRoleKey(role)
                    }
                }
            );

            downloadedInvoiceFile = downloadInvoiceFileResponse;
        } catch (error) {
            console.error("Error downloading file: ", error);
        }

        return downloadedInvoiceFile;
    };

    downloadFileAndTriggerDownload = async (fileId: string, fileName?: string) => {
        const downloadedFile = await this.downloadFile(fileId);
        const defaultFileName = i18next.t("labels.file");

        if (downloadedFile) {
            try {
                const fileBlob = downloadedFile.data;
                const objUrl = window.URL.createObjectURL(fileBlob);
                const fileInfo = await this.getFileInfo(fileId);
                // Trigger the download
                const a = document.createElement("a");
                a.href = objUrl;
                a.download = fileName ? fileName : fileInfo ? fileInfo.name : defaultFileName;

                document.body.appendChild(a);
                a.click();
                a.remove();
            } catch (error) {
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("stores.file_store.error_downloading_file"),
                    MessageType.ERROR
                );
            }
        }
    };

    getFileInfo = async (fileId: string): Promise<GetFileInfoById_file | undefined> => {
        let fileInfo: GetFileInfoById_file | undefined = undefined;

        const { data }: { data: GetFileInfoById } = await apolloClientInstance.query<
            GetFileInfoById,
            GetFileInfoByIdVariables
        >({
            query: GET_FILE_INFO_BY_ID,
            variables: {
                fileid: fileId
            }
        });

        if (data.file.length > 0) {
            const file = data.file[0];
            fileInfo = file;
        }

        return fileInfo;
    };

    triggerDownload = async (fileBlob: Blob, fileName?: string) => {
        const defaultFileName = i18next.t("labels.file");

        try {
            const objUrl = window.URL.createObjectURL(fileBlob);
            // Trigger the download
            const a = document.createElement("a");
            a.href = objUrl;
            a.download = fileName ?? defaultFileName;

            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(objUrl);
        } catch (error) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("stores.file_store.error_downloading_file"),
                MessageType.ERROR
            );
        }
    };

    uploadFileBulk = async (
        file: IFile,
        entityId: string,
        fileEntity: FILE_ENTITY,
        origin: FILE_ORIGIN = FILE_ORIGIN.CLOUD
    ): Promise<string | undefined> => {
        let newFileId = undefined;

        try {
            if (file.fileBlob === undefined) {
                console.error("File blob is undefined for file: ", file);
                return newFileId;
            }

            const requestBody = [
                {
                    version: "2.0",
                    id: file.fileId,
                    origin: origin,
                    mimetype: file.mimetype,
                    extension: file.extension,
                    name: file.name,
                    filedate: file.fileDate,
                    links: [
                        {
                            id: file.entityFileId,
                            entityid: entityId,
                            entity: fileEntity,
                            roleid: getRoleId(Role.MANAGER),
                            visible: true,
                            type: file.type
                        }
                    ]
                }
            ];

            const response = await Axios.getAuthInstance().put<FileUploadResult[]>(
                NetworkConfig.uploadFileBulkUrl,
                requestBody,
                {
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );
            const uploadResults = response.data;

            await Promise.all(
                uploadResults.map((result) => this.uploadFileInChunks(result.UploadUrl, file.fileBlob, file.mimetype))
            );

            newFileId = uploadResults[0]?.FileId;
        } catch (error) {
            console.error("Error occured while trying to upload image: ", error, file);
        }

        return newFileId;
    };

    uploadFilesBulkWithRoles = async (
        file: IFile,
        entityId: string,
        fileEntity: FILE_ENTITY,
        roles: Role[]
    ): Promise<string | undefined> => {
        let newFileId: string | undefined = undefined;

        try {
            if (file.fileBlob === undefined) {
                console.error("File blob is undefined for file: ", file);
                return newFileId;
            }

            const links =
                roles && roles.length > 0
                    ? roles.map((role) => ({
                          entityid: entityId,
                          entity: fileEntity,
                          roleid: getRoleId(role),
                          visible: true,
                          type: file.type
                      }))
                    : [
                          {
                              entityid: entityId,
                              entity: fileEntity,
                              roleid: getRoleId(Role.MANAGER),
                              visible: true,
                              type: file.type
                          }
                      ];

            const requestBody = [
                {
                    version: "2.0",
                    id: file.fileId,
                    origin: FILE_ORIGIN.CLOUD,
                    mimetype: file.mimetype,
                    extension: file.extension,
                    name: file.name,
                    filedate: file.fileDate,
                    links
                }
            ];

            const response = await Axios.getAuthInstance().put<FileUploadResult[]>(
                NetworkConfig.uploadFileBulkUrl,
                requestBody,
                {
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );
            const uploadResults = response.data;

            await Promise.all(
                uploadResults.map((result) => this.uploadFileInChunks(result.UploadUrl, file.fileBlob, file.mimetype))
            );

            newFileId = uploadResults[0]?.FileId;
        } catch (error) {
            console.error("Error occured while trying to upload image: ", error, file);
            throw error;
        }

        return newFileId;
    };

    uploadFileInChunks = async (
        sasUrl: string,
        fileBlob: File,
        mimetype?: string,
        onProgress?: (percent: number) => void
    ): Promise<void> => {
        const totalChunks = Math.ceil(fileBlob.size / this.CHUNK_SIZE);
        const blockIds: string[] = [];

        // 1. Alle Blöcke hochladen
        for (let i = 0; i < totalChunks; i++) {
            const start = i * this.CHUNK_SIZE;
            const end = Math.min(start + this.CHUNK_SIZE, fileBlob.size);
            const chunk = fileBlob.slice(start, end);

            // Block-ID muss base64 sein und für alle Blöcke gleich lang
            const blockId = btoa(String(i).padStart(6, "0"));
            blockIds.push(blockId);

            const blockUrl = `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;

            await Axios.getInstance().put(blockUrl, chunk, {
                headers: {
                    "x-ms-blob-type": "BlockBlob",
                    "Content-Type": mimetype
                },
                transformRequest: [(data) => data]
            });

            onProgress?.(((i + 1) / totalChunks) * 100);
        }

        // 2. Block-Liste committen (commitet die Blöcke in der angegebenen Reihenfolge)
        const blockListXml =
            '<?xml version="1.0" encoding="utf-8"?><BlockList>' +
            blockIds.map((id) => `<Latest>${id}</Latest>`).join("") +
            "</BlockList>";

        const commitUrl = `${sasUrl}&comp=blocklist`;

        await Axios.getInstance().put(commitUrl, blockListXml, {
            headers: {
                "x-ms-blob-content-type": mimetype,
                "Content-Type": "application/xml"
            },
            transformRequest: [(data) => data]
        });
    };
}
